import React, { useRef } from 'react';
import classNames from 'classnames';

import { ListingCard, NamedLink } from '../../../../components';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';

import SectionContainer from '../SectionContainer';

import css from './SectionLatestListings.module.css';

const KEY_CODE_ARROW_LEFT = 37;
const KEY_CODE_ARROW_RIGHT = 39;

const SectionLatestListings = props => {
  const intl = useIntl();

  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    appearance,
    options,
  } = props;

  const {
    latestListings = [],
    latestListingsInProgress = false,
    latestListingsError = null,
  } = options || {};

  const carouselRef = useRef(null);

  const visibleListings = latestListings.slice(0, 12);

  const scrollCarousel = direction => {
    const el = carouselRef.current;
    if (!el) return;

    const firstCard = el.querySelector('li');
    if (!firstCard) return;

    // Read the flex gap directly from the container (not marginRight on children)
    const gap = parseFloat(window.getComputedStyle(el).columnGap || '16') || 16;
    const cardWidth = firstCard.clientWidth + gap;

    // Scroll exactly one "page" worth of visible cards
    const visibleCount = Math.max(1, Math.floor(el.clientWidth / cardWidth));
    const step = cardWidth * visibleCount;
    const maxScrollLeft = el.scrollWidth - el.clientWidth;

    if (direction === 'next') {
      // Infinite loop: wrap to start when at the end
      if (el.scrollLeft >= maxScrollLeft - 1) {
        el.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        el.scrollBy({ left: step, behavior: 'smooth' });
      }
    } else {
      // Infinite loop: wrap to end when at the start
      if (el.scrollLeft <= 1) {
        el.scrollTo({ left: maxScrollLeft, behavior: 'smooth' });
      } else {
        el.scrollBy({ left: -step, behavior: 'smooth' });
      }
    }
  };

  const onCarouselKeyDown = event => {
    if (event.keyCode === KEY_CODE_ARROW_LEFT) {
      event.preventDefault();
      scrollCarousel('prev');
    }
    if (event.keyCode === KEY_CODE_ARROW_RIGHT) {
      event.preventDefault();
      scrollCarousel('next');
    }
  };

  const hasListings = !latestListingsInProgress && !latestListingsError && visibleListings.length > 0;

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={classNames(rootClassName || css.root)}
      appearance={appearance}
    >
      <header className={defaultClasses.sectionDetails}>
        <div className={css.headerRow}>
          <h3 className={classNames(defaultClasses.title, css.sectionTitle)}>
            <FormattedMessage id="LandingPage.latestListings.title" />
          </h3>
        </div>
      </header>

      {latestListingsInProgress ? (
        <p className={css.statusText}>
          <FormattedMessage id="LandingPage.latestListings.loading" />
        </p>
      ) : null}

      {!latestListingsInProgress && latestListingsError ? (
        <p className={css.statusText}>
          <FormattedMessage id="LandingPage.latestListings.error" />
        </p>
      ) : null}

      {!latestListingsInProgress && !latestListingsError && visibleListings.length === 0 ? (
        <p className={css.statusText}>
          <FormattedMessage id="LandingPage.latestListings.empty" />
        </p>
      ) : null}

      {hasListings ? (
        <div className={css.carouselWrap}>
          <button
            type="button"
            className={css.navButton}
            onClick={() => scrollCarousel('prev')}
            aria-label={intl.formatMessage({ id: 'LandingPage.latestListings.prev' })}
          >
            <span aria-hidden="true">&#10094;</span>
          </button>

          <ul
            ref={carouselRef}
            className={css.carousel}
            aria-label={intl.formatMessage({ id: 'LandingPage.latestListings.sectionAria' })}
            tabIndex={0}
            onKeyDown={onCarouselKeyDown}
          >
            {visibleListings.map(listing => (
              <li className={css.cardItem} key={listing.id.uuid}>
                <ListingCard
                  listing={listing}
                  renderSizes="(max-width: 549px) 82vw, (max-width: 1023px) 44vw, 24vw"
                />
              </li>
            ))}
          </ul>

          <button
            type="button"
            className={css.navButton}
            onClick={() => scrollCarousel('next')}
            aria-label={intl.formatMessage({ id: 'LandingPage.latestListings.next' })}
          >
            <span aria-hidden="true">&#10095;</span>
          </button>
        </div>
      ) : null}

      {hasListings ? (
        <div className={css.ctaWrap}>
          <NamedLink className={classNames(defaultClasses.ctaButton, css.ctaButton)} name="SearchPage">
            <FormattedMessage id="LandingPage.latestListings.ctaButton" />
          </NamedLink>
        </div>
      ) : null}
    </SectionContainer>
  );
};

export default SectionLatestListings;
